import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, Power, RotateCcw } from 'lucide-react';
import {
  SUBSCRIPTION_STATUSES,
  type Paginated,
  type PlatformBusinessDetail,
  type PlatformBusinessRow,
} from '@worki/shared';
import { api, buildQuery } from '../../lib/api.js';
import { useDebounced } from '../../lib/useDebounced.js';
import { currentLocale } from '../../i18n.js';
import { formatDate, formatDateTime } from '../../lib/format.js';
import { Button, EmptyState, Select, Spinner } from '../../components/ui.js';
import { PageSearch } from '../../components/PageSearch.js';
import { ConfirmDialog, Modal } from '../../components/Modal.js';
import { ACCESS_KEYS } from './Approvals.js';
import { AdminCard, Pill, Row, Stat, useAdminAction } from './parts.js';

/**
 * Every business on the platform, and what it is paying.
 *
 * Counts, never contents: staff see that a business has ninety customers and
 * never who they are. A customer of a customer is personal data collected for
 * that business's own purpose, and an employee browsing it has no purpose to
 * point at — see docs/01-features/13-platform-admin.md.
 *
 * Opening one is written into that business's own activity log, so a customer
 * who asks "who at Worki looked at our account" has an answer on their own
 * screen rather than having to take our word for it.
 */
const PAGE_SIZE = 30;

const STATUS_TONE = {
  trialing: 'info',
  active: 'good',
  past_due: 'warn',
  cancelled: 'neutral',
  expired: 'bad',
} as const;

export function BusinessesSection() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const [params, setParams] = useSearchParams();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const debounced = useDebounced(search, 300);

  /* A notification links straight to one business, so the id arrives in the
     URL rather than in state — which is also what makes the link sendable. */
  const openId = params.get('open');
  const setOpenId = (id: string | null) => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (id) next.set('open', id);
        else next.delete('open');
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => setPage(1), [debounced, status]);

  const list = useQuery({
    queryKey: ['platform', 'businesses', { debounced, status, page }],
    queryFn: () =>
      api.get<Paginated<PlatformBusinessRow>>(
        `/platform/businesses${buildQuery({ q: debounced, status, page, pageSize: PAGE_SIZE })}`,
      ),
  });

  const rows = list.data?.items ?? [];
  const pages = Math.max(1, Math.ceil((list.data?.total ?? 0) / PAGE_SIZE));

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2">
        <PageSearch value={search} onChange={setSearch} placeholder={t('platform.searchPlaceholder')} />
        <Select value={status} aria-label={t('platform.status')} onChange={setStatus} className="sm:w-52">
          <option value="">{t('platform.allStatuses')}</option>
          {SUBSCRIPTION_STATUSES.map((option) => (
            <option key={option} value={option}>
              {t(`billing.status.${option}`)}
            </option>
          ))}
        </Select>
      </div>

      {list.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState icon={<Building2 className="h-7 w-7" />} title={t('platform.empty')} />
      ) : (
        <div className="space-y-2">
          {rows.map((business) => (
            <Row
              key={business.id}
              icon={<Building2 className="h-4 w-4" aria-hidden />}
              title={
                <span className="flex items-center gap-2">
                  <span className="truncate">{business.name}</span>
                  <Pill tone={STATUS_TONE[business.status as keyof typeof STATUS_TONE] ?? 'neutral'}>
                    {t(`billing.status.${business.status}`, { defaultValue: business.status })}
                  </Pill>
                  {business.access !== 'active' && (
                    <Pill tone="warn">
                      {t(`admin.approvals.${business.access === 'pending' ? 'pending' : 'suspended'}`)}
                    </Pill>
                  )}
                </span>
              }
              subtitle={
                <span dir="ltr" className="inline-block max-w-full truncate">
                  {[business.email, t(`billing.plans.${business.plan}`)].filter(Boolean).join(' · ')}
                </span>
              }
              meta={
                business.lastActivityAt ? formatDate(business.lastActivityAt, locale) : t('platform.never')
              }
              onClick={() => setOpenId(business.id)}
            />
          ))}
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground">
          <Button variant="ghost" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>
            {t('common.previous')}
          </Button>
          <span className="ltr-nums">
            {page} / {pages}
          </span>
          <Button variant="ghost" disabled={page >= pages} onClick={() => setPage((current) => current + 1)}>
            {t('common.next')}
          </Button>
        </div>
      )}

      <p className="text-center text-xs text-muted-foreground">{t('platform.readOnly')}</p>

      <BusinessDialog id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

function BusinessDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const action = useAdminAction(ACCESS_KEYS);

  const detail = useQuery({
    queryKey: ['platform', 'business', id],
    queryFn: () => api.get<PlatformBusinessDetail>(`/platform/businesses/${id ?? ''}`),
    enabled: id !== null,
  });

  const business = detail.data;

  const setAccess = (access: 'active' | 'suspended', successKey: string) =>
    action.run(() => api.post(`/platform/businesses/${id ?? ''}/access`, { access }), successKey);

  const field = (label: string, value: string | null, ltr = false) => (
    <div className="min-w-0">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className="text-sm truncate" {...(ltr ? { dir: 'ltr' as const } : {})}>
        {value ?? '—'}
      </p>
    </div>
  );

  return (
    <Modal open={id !== null} title={t('platform.detailTitle')} onClose={onClose} size="wide">
      {!business ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <strong className="text-sm">{business.name}</strong>
            <Pill tone={STATUS_TONE[business.status as keyof typeof STATUS_TONE] ?? 'neutral'}>
              {t(`billing.status.${business.status}`, { defaultValue: business.status })}
            </Pill>
            <span className="text-xs text-muted-foreground">{t(`billing.plans.${business.plan}`)}</span>
          </div>

          <AdminCard title={t('platform.contact')}>
            <div className="grid grid-cols-2 gap-3">
              {field(t('customer.email'), business.email, true)}
              {field(t('customer.phone'), business.phone, true)}
              {field(t('platform.slug'), business.slug, true)}
              {field(t('platform.joined'), formatDate(business.createdAt, locale), true)}
              {field(t('platform.provider'), business.provider, true)}
              {field(t('platform.trialEnds'), formatDate(business.trialEndsAt, locale), true)}
              {field(t('platform.periodEnds'), formatDate(business.currentPeriodEnd, locale), true)}
              {field(
                t('platform.lastActivity'),
                business.lastActivityAt ? formatDateTime(business.lastActivityAt, locale) : t('platform.never'),
                business.lastActivityAt !== null,
              )}
            </div>
          </AdminCard>

          <AdminCard title={t('platform.usage')}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label={t('platform.mailAccounts')} value={business.usage.connectedMailAccounts} tone="info" />
              <Stat label={t('platform.galleries')} value={business.usage.galleries} tone="muted" />
              <Stat label={t('platform.quotesThisMonth')} value={business.usage.quotesThisMonth} tone="good" />
              <Stat label={t('platform.customers')} value={business.customers} tone="muted" />
              <Stat label={t('platform.quotes')} value={business.quotes} tone="muted" />
              <Stat label={t('platform.inquiries')} value={business.inquiries} tone="muted" />
              <Stat label={t('platform.invoices')} value={business.invoices} tone="muted" />
            </div>
          </AdminCard>

          {/*
            ADM-012 — how this account got to the status in the header.
            A customer asking why they were charged, or why their access
            stopped, is asking about the path and not the destination; the
            subscription row only remembers where they ended up.
          */}
          <AdminCard title={t('platform.subscriptionHistory')}>
            {business.subscriptionHistory.length === 0 ? (
              <p className="text-xs text-muted-foreground">{t('platform.noSubscriptionHistory')}</p>
            ) : (
              <ol className="space-y-1.5">
                {business.subscriptionHistory.map((event) => (
                  <li key={event.id} className="flex items-baseline justify-between gap-3 text-xs">
                    <span className="min-w-0">
                      {/* The action is a key we ship copy for; an event from a
                          provider we have not written wording for yet reads as
                          itself rather than as a blank line. */}
                      <span className="font-medium">
                        {t(`platform.billingEvent.${event.action.replace('billing.', '')}`, {
                          defaultValue: event.action,
                        })}
                      </span>
                      {event.plan && (
                        <span className="text-muted-foreground"> · {t(`billing.plans.${event.plan}`)}</span>
                      )}
                      <span className="block text-[11px] text-muted-foreground truncate" dir="ltr">
                        {event.actorLabel}
                      </span>
                    </span>
                    <span className="text-[11px] text-muted-foreground shrink-0 ltr-nums">
                      {formatDateTime(event.at, locale)}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </AdminCard>

          {/*
            ADM-003 — the one control on this screen that changes anything, and
            it changes only whether the door is open. Nothing is deleted and the
            subscription is untouched, so the decision is reversible in one
            press — which is precisely why it is the tool offered here.
          */}
          <div className="flex justify-end">
            {business.access === 'suspended' ? (
              <button
                type="button"
                disabled={action.busy}
                onClick={() => void setAccess('active', 'admin.approvals.restored')}
                className="h-9 px-3 rounded-lg border border-border bg-card text-xs font-medium flex items-center gap-1.5 hover:bg-muted disabled:opacity-50 transition-colors"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                {t('admin.approvals.restore')}
              </button>
            ) : (
              <button
                type="button"
                disabled={action.busy}
                onClick={() => setConfirmSuspend(true)}
                className="h-9 px-3 rounded-lg bg-destructive/10 text-destructive text-xs font-medium flex items-center gap-1.5 hover:bg-destructive/15 disabled:opacity-50 transition-colors"
              >
                <Power className="h-3.5 w-3.5" />
                {t('admin.approvals.suspend')}
              </button>
            )}
          </div>

          <ConfirmDialog
            open={confirmSuspend}
            title={t('admin.approvals.suspend')}
            message={t('admin.approvals.suspendConfirm', { name: business.name })}
            confirmLabel={t('admin.approvals.suspend')}
            loading={action.busy}
            onCancel={() => setConfirmSuspend(false)}
            onConfirm={() => {
              void setAccess('suspended', 'admin.approvals.wasSuspended').then((ok) => {
                if (ok) setConfirmSuspend(false);
              });
            }}
          />
        </div>
      )}
    </Modal>
  );
}
